package com.evoapps.evoshift.widgets

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.ui.Alignment
import androidx.compose.ui.semantics.clearAndSetSemantics
import com.evoapps.evoshift.ui.theme.LocalReducedMotion
import com.evoapps.evoshift.ui.theme.Motion
import com.evoapps.evoshift.ui.theme.motionTween
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.GoldMetallicBrush
import com.evoapps.evoshift.ui.adaptive.handCursor

/**
 * A Material [Button] whose container is a [brush] (the metallic gold, the red of destructive
 * actions) filling [shape] exactly once, in every state.
 *
 * The brush is painted by the modifier and the Material container stays transparent, so the
 * button must have no elevation (SH-58): a filled Button rises to 1 dp while hovered (on the web
 * the pointer stays over it after a click), and its Surface then draws a shadow for the
 * transparent container on top of the brush. Skia (web, iOS) draws that shadow through the
 * transparent shape: a darker rim around a lighter, square-cornered rectangle behind the icon and
 * text - a button within a button. Without elevation nothing sits between the brush and the
 * content; the hover/press state layer stays clipped to [shape].
 */
@Composable
fun BrushButton(
    onClick: () -> Unit,
    brush: Brush,
    shape: Shape,
    contentColor: Color,
    disabledContentColor: Color,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    contentPadding: PaddingValues = ButtonDefaults.ContentPadding,
    content: @Composable RowScope.() -> Unit
) {
    Button(
        onClick = onClick,
        enabled = enabled,
        shape = shape,
        colors = ButtonDefaults.buttonColors(
            containerColor = Color.Transparent,
            contentColor = contentColor,
            disabledContainerColor = Color.Transparent,
            disabledContentColor = disabledContentColor
        ),
        elevation = null,
        contentPadding = contentPadding,
        modifier = modifier
            .clip(shape)
            .background(brush, shape)
            .handCursor(),
        content = content
    )
}

/**
 * The primary action on the navy brand screens (onboarding, plans): metallic gold with navy
 * text, as the setup screen's button. Grows with large fonts; shows progress while [isBusy].
 */
@Composable
fun GoldButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    isBusy: Boolean = false
) {
    BrushButton(
        onClick = onClick,
        brush = GoldMetallicBrush,
        shape = RoundedCornerShape(28.dp),
        contentColor = DeepNavy,
        disabledContentColor = DeepNavy.copy(alpha = 0.6f),
        enabled = enabled && !isBusy,
        modifier = modifier
            .heightIn(min = 54.dp)
            .alpha(if (enabled || isBusy) 1f else 0.5f)
    ) {
        // SH-158: the label and the spinner cross-fade in a box as wide as the label, so the
        // button keeps its width while busy.
        Box(contentAlignment = Alignment.Center) {
            Text(text, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center, modifier = Modifier.alpha(0f).clearAndSetSemantics {})
            val reduced = LocalReducedMotion.current
            AnimatedContent(
                targetState = isBusy,
                transitionSpec = { fadeIn(motionTween(Motion.SHORT_MS, reduced)) togetherWith fadeOut(motionTween(Motion.SHORT_MS, reduced)) },
                contentAlignment = Alignment.Center,
                label = "goldButtonBusy"
            ) { busy ->
                if (busy) {
                    CircularProgressIndicator(Modifier.size(22.dp), color = DeepNavy, strokeWidth = 2.dp)
                } else {
                    Text(text, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center)
                }
            }
        }
    }
}
